import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LayoutRectangle, StyleSheet, TouchableOpacity } from 'react-native';
import { Tooltip, BaseProps as TooltipBaseProps } from '@/components/tooltip';

export interface TooltipContextType {
  createTooltip: (tooltipProps: TooltipBaseProps) => void;
  removeTooltip: () => void;
}

export const TooltipContext = createContext<TooltipContextType | null>(null);

export const useTooltips = () => {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltips requires a TooltipContextProvider');
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
  // Sometimes the tooltip target is animated so this can be set to false until
  // the animation completes
  enabled?: boolean;
}

export const TooltipContextProvider: React.FC<Props> = ({
  children,
  enabled = true,
}) => {
  const containerRef =
    useRef<React.ComponentRef<typeof TouchableOpacity>>(null);

  const [closing, setClosing] = useState(false);
  const [containerLayout, setContainerLayout] =
    useState<LayoutRectangle | null>(null);
  const [tooltipProps, setTooltipProps] = useState<TooltipBaseProps | null>(
    null,
  );

  const removeTooltip = useCallback(() => {
    setClosing(false);
    setTooltipProps(null);
  }, [setTooltipProps]);

  const value = useMemo<TooltipContextType>(
    () => ({
      createTooltip: setTooltipProps,
      removeTooltip,
    }),
    [removeTooltip],
  );

  const content = !tooltipProps ? null : tooltipProps.hideClosingLayer ? (
    <Tooltip
      {...tooltipProps}
      closing={closing}
      containerLayout={containerLayout}
      onClose={() => {
        removeTooltip();
        tooltipProps.onClose();
      }}
    />
  ) : (
    <TouchableOpacity
      activeOpacity={1}
      onLayout={() => {
        containerRef.current?.measureInWindow((x, y, width, height) => {
          setContainerLayout({ height, width, x, y });
        });
      }}
      onPress={() => setClosing(true)}
      ref={containerRef}
      style={StyleSheet.absoluteFillObject}
    >
      {containerLayout ? (
        <Tooltip
          {...tooltipProps}
          closing={closing}
          containerLayout={containerLayout}
          onClose={() => {
            removeTooltip();
            tooltipProps.onClose();
          }}
        />
      ) : null}
    </TouchableOpacity>
  );

  return (
    <TooltipContext.Provider value={value}>
      {children}
      {enabled && tooltipProps?.visible ? content : null}
    </TooltipContext.Provider>
  );
};
